import React, { useState, useEffect } from 'react'
import { useFileUpload } from '../hooks/useFileUpload'
import { fileUploadService } from '../services/fileUploadService'

interface FileUploadPanelProps {
  onFilesUploaded: (files: { file: File; label: string; category: string }[]) => void
}

const FileUploadPanel: React.FC<FileUploadPanelProps> = ({ onFilesUploaded }) => {
  const {
    uploadState,
    fileInputRef,
    handleFileInputChange,
    openFilePicker,
    clearError,
    clearUploadedFiles,
    removeUploadedFile,
    formatFileSize,
    formatDuration
  } = useFileUpload()

  const [showUploadForm, setShowUploadForm] = useState(false)
  const [fileLabels, setFileLabels] = useState<{ [key: number]: string }>({})
  const [fileCategories, setFileCategories] = useState<{ [key: number]: string }>({})
  const categories = [
    { value: 'drums', label: '🥁 Drums & Percussion' },
    { value: 'instruments', label: '🎵 Musical Instruments' },
    { value: 'effects', label: '✨ Sound Effects' },
    { value: 'nature', label: '🌿 Nature Sounds' },
    { value: 'custom', label: '🎤 Custom' }
  ]

  const handleFilePickerOpen = () => {
    openFilePicker()
  }

  const handleFileInputChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    const options = fileUploadService.getDefaultOptions()
    handleFileInputChange(event, options)

    // Show upload form when files are selected
    if (event.target.files && event.target.files.length > 0) {
      setShowUploadForm(true)

      // Initialize labels and categories for new files
      const newLabels: { [key: number]: string } = {}
      const newCategories: { [key: number]: string } = {}

      Array.from(event.target.files).forEach((file, index) => {
        const baseName = file.name.replace(/\.[^/.]+$/, '') // Remove extension
        newLabels[index] = baseName
        newCategories[index] = 'custom' // Default category
      })

      setFileLabels(newLabels)
      setFileCategories(newCategories)
    }
  }

  const handleLabelChange = (index: number, label: string) => {
    setFileLabels(prev => ({ ...prev, [index]: label }))
  }

  const handleCategoryChange = (index: number, category: string) => {
    setFileCategories(prev => ({ ...prev, [index]: category }))
  }

  const handleSaveFiles = () => {
    const filesToSave = uploadState.uploadedFiles.map((uploadedFile, index) => ({
      file: uploadedFile.file,
      label: fileLabels[index] || uploadedFile.file.name.replace(/\.[^/.]+$/, ''),
      category: fileCategories[index] || 'custom'
    }))

    onFilesUploaded(filesToSave)

    // Reset state
    clearUploadedFiles()
    setShowUploadForm(false)
    setFileLabels({})
    setFileCategories({})
  }

  const handleCancelUpload = () => {
    clearUploadedFiles()
    setShowUploadForm(false)
    setFileLabels({})
    setFileCategories({})
  }

  const handleRemoveFile = (index: number) => {
    removeUploadedFile(index)

    // Update labels and categories
    const newLabels = { ...fileLabels }
    const newCategories = { ...fileCategories }
    delete newLabels[index]
    delete newCategories[index]

    setFileLabels(newLabels)
    setFileCategories(newCategories)
  }

  if (showUploadForm && uploadState.uploadedFiles.length > 0) {
    return (
      <div className="file-upload-panel">
        <div className="file-upload-form">
          <h3>Upload Audio Files</h3>
          <p>Add labels and categories for your files:</p>

          {uploadState.error && (
            <div className="file-upload-error">
              <p>{uploadState.error}</p>
              <button onClick={clearError} className="file-upload-error-dismiss">
                Dismiss
              </button>
            </div>
          )}

          <div className="file-upload-list">
            {uploadState.uploadedFiles.map((uploadedFile, index) => (
              <div key={index} className="file-upload-item">
                <div className="file-upload-preview">
                  <div className="file-upload-icon">🎵</div>
                  <div className="file-upload-info">
                    <div className="file-upload-name">{uploadedFile.file.name}</div>
                    <div className="file-upload-details">
                      {formatFileSize(uploadedFile.size)}
                      {uploadedFile.duration && ` • ${formatDuration(uploadedFile.duration)}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="file-upload-remove"
                    title="Remove file"
                  >
                    ✕
                  </button>
                </div>

                <div className="file-upload-fields">
                  <div className="file-upload-field">
                    <label htmlFor={`label-${index}`}>Label:</label>
                    <input
                      id={`label-${index}`}
                      type="text"
                      value={fileLabels[index] || ''}
                      onChange={(e) => handleLabelChange(index, e.target.value)}
                      placeholder="Enter sound name..."
                      maxLength={50}
                    />
                  </div>

                  <div className="file-upload-field">
                    <label htmlFor={`category-${index}`}>Category:</label>
                    <select
                      id={`category-${index}`}
                      value={fileCategories[index] || 'custom'}
                      onChange={(e) => handleCategoryChange(index, e.target.value)}
                    >
                      {categories.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="file-upload-actions">
            <button onClick={handleCancelUpload} className="file-upload-cancel-button">
              Cancel
            </button>
            <button
              onClick={handleSaveFiles}
              className="file-upload-save-button"
              disabled={uploadState.isUploading}
            >
              {uploadState.isUploading ? 'Processing...' : 'Save Files'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="file-upload-panel">
      <div className="file-upload-controls">
        <h3>Upload Audio Files</h3>
        <p>Select audio files from your device to add to your sound board.</p>

        {uploadState.error && (
          <div className="file-upload-error">
            <p>{uploadState.error}</p>
            <button onClick={clearError} className="file-upload-error-dismiss">
              Dismiss
            </button>
          </div>
        )}

        <div className="file-upload-actions">
          <button
            onClick={handleFilePickerOpen}
            className="file-upload-button"
            disabled={uploadState.isUploading}
          >
            📁 Choose Audio Files
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.webm,.mp4,.aac,.flac,.m4a"
            multiple
            onChange={handleFileInputChangeEvent}
            style={{ display: 'none' }}
          />
        </div>

        <div className="file-upload-info">
          <p><strong>Supported formats:</strong> MP3, WAV, OGG, WebM, MP4, AAC, FLAC</p>
          <p><strong>Max file size:</strong> 10MB per file</p>
          <p className="ios-note">📱 <strong>iOS users:</strong> Tap "Choose Audio Files" to access your Files app, iCloud Drive, or other cloud storage.</p>
        </div>
      </div>
    </div>
  )
}

export default FileUploadPanel
